import React, { useState } from 'react';
import {
  Card,
  Button,
  Select,
  DatePicker,
  Space,
  Row,
  Col,
  Statistic,
  Progress,
  List,
  Badge,
  Table,
  Tag,
  Modal,
  Tabs,
  Divider,
} from 'antd';
import {
  BarChartOutlined,
  DownloadOutlined,
  EyeOutlined,
  LineChartOutlined,
  PieChartOutlined,
  RiseOutlined,
  FallOutlined,
} from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Reports: React.FC = () => {
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [dateRange, setDateRange] = useState<any>([]);
  const [reportType, setReportType] = useState('user');

  // 模拟报表数据
  const userReportData = [
    {
      date: '2025-10-23',
      newUsers: 156,
      activeUsers: 3240,
      totalUsers: 12580,
      retentionRate: 85.6,
      avgSessionTime: 25.3,
    },
    {
      date: '2025-10-22',
      newUsers: 142,
      activeUsers: 3156,
      totalUsers: 12424,
      retentionRate: 84.2,
      avgSessionTime: 24.8,
    },
    {
      date: '2025-10-21',
      newUsers: 168,
      activeUsers: 3289,
      totalUsers: 12282,
      retentionRate: 86.1,
      avgSessionTime: 26.1,
    },
  ];

  const conversationReportData = [
    {
      date: '2025-10-23',
      totalConversations: 12560,
      successfulConversations: 11832,
      avgResponseTime: 1.2,
      satisfaction: 4.5,
      agentUsage: {
        '气氛活跃Bot': 3240,
        '产品介绍Bot': 2156,
        '客服Bot': 4560,
        '情感交流Bot': 1234,
        '搞笑Bot': 890,
        '擦边Bot': 480,
      },
    },
    {
      date: '2025-10-22',
      totalConversations: 11890,
      successfulConversations: 11234,
      avgResponseTime: 1.3,
      satisfaction: 4.4,
      agentUsage: {
        '气氛活跃Bot': 3056,
        '产品介绍Bot': 2034,
        '客服Bot': 4320,
        '情感交流Bot': 1156,
        '搞笑Bot': 824,
        '擦边Bot': 500,
      },
    },
  ];

  const conversionReportData = [
    {
      date: '2025-10-23',
      gameConversions: 1256,
      gameConversionRate: 12.5,
      depositConversions: 456,
      depositConversionRate: 4.5,
      vipConversions: 89,
      vipConversionRate: 0.9,
      totalRevenue: 45680,
    },
    {
      date: '2025-10-22',
      gameConversions: 1189,
      gameConversionRate: 11.8,
      depositConversions: 423,
      depositConversionRate: 4.2,
      vipConversions: 78,
      vipConversionRate: 0.8,
      totalRevenue: 42350,
    },
  ];

  const systemReportData = [
    {
      date: '2025-10-23',
      availability: 99.9,
      avgResponseTime: 0.8,
      errorRate: 0.1,
      tokenUsage: 1256000,
      cost: 45.6,
      cacheHitRate: 92.5,
    },
    {
      date: '2025-10-22',
      availability: 99.8,
      avgResponseTime: 0.9,
      errorRate: 0.2,
      tokenUsage: 1189000,
      cost: 43.2,
      cacheHitRate: 91.8,
    },
  ];

  const reportStats = [
    { title: '总用户数', value: 12580, icon: <BarChartOutlined />, trend: 'up', change: '+5.2%' },
    { title: '今日对话数', value: 12560, icon: <LineChartOutlined />, trend: 'up', change: '+8.1%' },
    { title: '转化率', value: '12.5%', icon: <PieChartOutlined />, trend: 'up', change: '+1.2%' },
    { title: '系统可用性', value: '99.9%', icon: <BarChartOutlined />, trend: 'up', change: '+0.1%' },
  ];

  const topAgents = [
    { agent: '客服Bot', usage: 4560, successRate: 98.7, satisfaction: 4.8 },
    { agent: '气氛活跃Bot', usage: 3240, successRate: 94.5, satisfaction: 4.6 },
    { agent: '产品介绍Bot', usage: 2156, successRate: 92.3, satisfaction: 4.4 },
    { agent: '情感交流Bot', usage: 1234, successRate: 89.2, satisfaction: 4.2 },
  ];

  const handleExportReport = () => {
    // 模拟导出功能
    const reportData = {
      type: reportType,
      dateRange: dateRange,
      data: reportType === 'user' ? userReportData : 
            reportType === 'conversation' ? conversationReportData :
            reportType === 'conversion' ? conversionReportData : systemReportData
    };
    
    console.log('导出报表:', reportData);
    
    // 模拟下载
    const element = document.createElement('a');
    const file = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    element.href = URL.createObjectURL(file);
    element.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleViewDetails = (report: any) => {
    setSelectedReport(report);
    setDetailModalVisible(true);
  };

  const getCurrentData = (): any[] => {
    switch (reportType) {
      case 'user':
        return userReportData;
      case 'conversation':
        return conversationReportData;
      case 'conversion':
        return conversionReportData;
      case 'system':
        return systemReportData;
      default:
        return userReportData;
    }
  };

  const getColumns = () => {
    const baseColumns = [
      {
        title: '日期',
        dataIndex: 'date',
        key: 'date',
        width: 120,
      },
    ];

    switch (reportType) {
      case 'user':
        return [
          ...baseColumns,
          { title: '新增用户', dataIndex: 'newUsers', key: 'newUsers', width: 100 },
          { title: '活跃用户', dataIndex: 'activeUsers', key: 'activeUsers', width: 100 },
          { title: '总用户数', dataIndex: 'totalUsers', key: 'totalUsers', width: 100 },
          { 
            title: '留存率', 
            dataIndex: 'retentionRate', 
            key: 'retentionRate', 
            width: 100,
            render: (rate: number) => (
              <Progress percent={rate} size="small" format={() => `${rate}%`} />
            )
          },
          { 
            title: '平均会话时长', 
            dataIndex: 'avgSessionTime', 
            key: 'avgSessionTime', 
            width: 120,
            render: (time: number) => `${time}分钟`
          },
          {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: any, record: any) => (
              <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)}>
                详情
              </Button>
            ),
          },
        ];
      case 'conversation':
        return [
          ...baseColumns,
          { title: '总对话数', dataIndex: 'totalConversations', key: 'totalConversations', width: 100 },
          { title: '成功对话数', dataIndex: 'successfulConversations', key: 'successfulConversations', width: 100 },
          { 
            title: '平均响应时间', 
            dataIndex: 'avgResponseTime', 
            key: 'avgResponseTime', 
            width: 120,
            render: (time: number) => `${time}秒`
          },
          { 
            title: '满意度', 
            dataIndex: 'satisfaction', 
            key: 'satisfaction', 
            width: 100,
            render: (score: number) => (
              <Progress percent={score * 20} size="small" format={() => `${score}/5`} />
            )
          },
          {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: any, record: any) => (
              <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)}>
                详情
              </Button>
            ),
          },
        ];
      case 'conversion':
        return [
          ...baseColumns,
          { title: '游戏转化数', dataIndex: 'gameConversions', key: 'gameConversions', width: 100 },
          { 
            title: '游戏转化率', 
            dataIndex: 'gameConversionRate', 
            key: 'gameConversionRate', 
            width: 100,
            render: (rate: number) => (
              <Progress percent={rate} size="small" format={() => `${rate}%`} />
            )
          },
          { title: '充值转化数', dataIndex: 'depositConversions', key: 'depositConversions', width: 100 },
          { 
            title: '充值转化率', 
            dataIndex: 'depositConversionRate', 
            key: 'depositConversionRate', 
            width: 100,
            render: (rate: number) => (
              <Progress percent={rate} size="small" format={() => `${rate}%`} />
            )
          },
          { 
            title: '总收入', 
            dataIndex: 'totalRevenue', 
            key: 'totalRevenue', 
            width: 100,
            render: (amount: number) => `¥${amount.toLocaleString()}`
          },
          {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: any, record: any) => (
              <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)}>
                详情
              </Button>
            ),
          },
        ];
      case 'system':
        return [
          ...baseColumns,
          { 
            title: '可用性', 
            dataIndex: 'availability', 
            key: 'availability', 
            width: 100,
            render: (rate: number) => (
              <Progress percent={rate} size="small" format={() => `${rate}%`} />
            )
          },
          { 
            title: '平均响应时间', 
            dataIndex: 'avgResponseTime', 
            key: 'avgResponseTime', 
            width: 120,
            render: (time: number) => `${time}秒`
          },
          { 
            title: '错误率', 
            dataIndex: 'errorRate', 
            key: 'errorRate', 
            width: 100,
            render: (rate: number) => (
              <Progress percent={rate} size="small" format={() => `${rate}%`} strokeColor="#ff4d4f" />
            )
          },
          { 
            title: 'Token使用', 
            dataIndex: 'tokenUsage', 
            key: 'tokenUsage', 
            width: 120,
            render: (tokens: number) => tokens.toLocaleString()
          },
          { 
            title: '成本', 
            dataIndex: 'cost', 
            key: 'cost', 
            width: 100,
            render: (cost: number) => `$${cost}`
          },
          {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: any, record: any) => (
              <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)}>
                详情
              </Button>
            ),
          },
        ];
      default:
        return baseColumns;
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>后台报表</h1>
        <p>常规数据的分析和呈现，包括用户规模、平均对话条数、闲聊占比、转化比例等业务指标</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {reportStats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <div className="stats-card">
                <div style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }}>
                  {stat.icon}
                </div>
                <div className="stats-number">{stat.value}</div>
                <div className="stats-label">{stat.title}</div>
                <div style={{ marginTop: 8 }}>
                  <Tag color={stat.trend === 'up' ? 'green' : 'red'} icon={stat.trend === 'up' ? <RiseOutlined /> : <FallOutlined />}>
                    {stat.change}
                  </Tag>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 报表控制 */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Space>
              <span>报表类型：</span>
              <Select
                value={reportType}
                onChange={setReportType}
                style={{ width: 150 }}
              >
                <Option value="user">用户报表</Option>
                <Option value="conversation">对话报表</Option>
                <Option value="conversion">转化报表</Option>
                <Option value="system">系统报表</Option>
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={8}>
            <Space>
              <span>时间范围：</span>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                style={{ width: 200 }}
              />
            </Space>
          </Col>
          <Col xs={24} sm={8}>
            <Space>
              <Button type="primary" icon={<BarChartOutlined />}>
                刷新数据
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExportReport}>
                导出报表
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        {/* 报表数据表格 */}
        <Col xs={24} lg={16}>
          <Card title={`${reportType === 'user' ? '用户' : reportType === 'conversation' ? '对话' : reportType === 'conversion' ? '转化' : '系统'}报表数据`}>
            <Table
              columns={getColumns()}
              dataSource={getCurrentData()}
              rowKey="date"
              scroll={{ x: 1400 }}
              pagination={{
                total: getCurrentData().length,
                pageSize: 10,
                showTotal: (total) => `共 ${total} 条记录`,
              }}
            />
          </Card>
        </Col>

        {/* 智能体使用统计 */}
        <Col xs={24} lg={8}>
          <Card title="智能体使用统计" extra={<Button type="link">查看全部</Button>}>
            <List
              size="small"
              dataSource={topAgents}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{item.agent}</span>
                        <Badge count={item.usage} style={{ backgroundColor: '#52c41a' }} />
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 4 }}>
                          成功率: {item.successRate}% | 满意度: {item.satisfaction}/5
                        </div>
                        <Progress percent={item.successRate} size="small" />
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>

          <Card title="趋势分析" style={{ marginTop: 16 }}>
            <div style={{ height: 200, background: '#f5f5f5', borderRadius: 8, padding: 16 }}>
              <div style={{ textAlign: 'center', padding: 50, color: '#999' }}>
                <LineChartOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <div>趋势图表</div>
                <div style={{ fontSize: 12 }}>显示数据变化趋势</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 详情模态框 */}
      <Modal
        title="报表详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
          <Button key="export" icon={<DownloadOutlined />}>
            导出详情
          </Button>,
        ]}
      >
        {selectedReport && (
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="基础数据" key="1">
              <Row gutter={[16, 16]}>
                {Object.entries(selectedReport).map(([key, value]) => {
                  if (key === 'agentUsage') return null;
                  return (
                    <Col span={8} key={key}>
                      <Card size="small">
                        <Statistic
                          title={key}
                          value={typeof value === 'number' ? value : String(value)}
                          formatter={(val) => {
                            if (typeof val === 'number' && val < 1) {
                              return `${(val * 100).toFixed(1)}%`;
                            }
                            return val;
                          }}
                        />
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Tabs.TabPane>
            <Tabs.TabPane tab="智能体使用" key="2">
              {selectedReport.agentUsage && (
                <div>
                  <h4>智能体使用分布</h4>
                  <List
                    size="small"
                    dataSource={Object.entries(selectedReport.agentUsage)}
                    renderItem={([agent, usage]) => (
                      <List.Item>
                        <div style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontWeight: 'bold' }}>{agent}</span>
                            <span style={{ color: '#666' }}>使用次数: {String(usage)}</span>
                          </div>
                          <Progress 
                            percent={(usage as number) / Math.max(...Object.values(selectedReport.agentUsage) as number[]) * 100} 
                            size="small" 
                            showInfo={false}
                          />
                        </div>
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </Tabs.TabPane>
            <Tabs.TabPane tab="趋势分析" key="3">
              <div style={{ padding: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card size="small" title="用户增长趋势">
                      <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                            {selectedReport.newUsers || 0}
                          </div>
                          <div style={{ color: '#666' }}>新增用户</div>
                          <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                            较昨日 {selectedReport.newUsers > 100 ? '+' : ''}{Math.floor(Math.random() * 20 - 10)}%
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="对话成功率趋势">
                      <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                            {selectedReport.satisfaction || 0}%
                          </div>
                          <div style={{ color: '#666' }}>成功率</div>
                          <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                            较昨日 {selectedReport.satisfaction > 90 ? '+' : ''}{Math.floor(Math.random() * 5 - 2)}%
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="智能体使用分布">
                      <div style={{ height: 200 }}>
                        <List
                          size="small"
                          dataSource={Object.entries(selectedReport.agentUsage || {})}
                          renderItem={([agent, usage]) => (
                            <List.Item>
                              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                <span>{agent}</span>
                                <span style={{ fontWeight: 'bold' }}>{String(usage)}</span>
                              </div>
                            </List.Item>
                          )}
                        />
                      </div>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="响应时间趋势">
                      <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fa8c16' }}>
                            {selectedReport.avgResponseTime || 0}s
                          </div>
                          <div style={{ color: '#666' }}>平均响应时间</div>
                          <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                            较昨日 {selectedReport.avgResponseTime < 2 ? '-' : '+'}{Math.floor(Math.random() * 0.5 * 10) / 10}s
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>
                
                {/* 详细趋势图表 */}
                <Card size="small" title="7天趋势对比" style={{ marginTop: 16 }}>
                  <div style={{ height: 300, padding: 16 }}>
                    <Row gutter={[16, 16]}>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: 20, background: '#f0f9ff', borderRadius: 8 }}>
                          <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                            {Math.floor(Math.random() * 1000 + 2000)}
                          </div>
                          <div style={{ color: '#666', marginTop: 4 }}>总对话数</div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: 20, background: '#f6ffed', borderRadius: 8 }}>
                          <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                            {Math.floor(Math.random() * 200 + 150)}
                          </div>
                          <div style={{ color: '#666', marginTop: 4 }}>新增用户</div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: 20, background: '#fff7e6', borderRadius: 8 }}>
                          <div style={{ fontSize: 20, fontWeight: 'bold', color: '#fa8c16' }}>
                            {Math.floor(Math.random() * 10 + 90)}%
                          </div>
                          <div style={{ color: '#666', marginTop: 4 }}>成功率</div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: 20, background: '#f9f0ff', borderRadius: 8 }}>
                          <div style={{ fontSize: 20, fontWeight: 'bold', color: '#722ed1' }}>
                            {Math.floor(Math.random() * 2 + 1)}.{(Math.random() * 10).toFixed(1)}s
                          </div>
                          <div style={{ color: '#666', marginTop: 4 }}>响应时间</div>
                        </div>
                      </Col>
                    </Row>
                    
                    {/* 模拟趋势线 */}
                    <div style={{ marginTop: 20, padding: 16, background: '#fafafa', borderRadius: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', height: 100 }}>
                        {[65, 72, 68, 75, 80, 78, 85].map((height, index) => (
                          <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div 
                              style={{ 
                                width: 20, 
                                height: height, 
                                background: '#1890ff', 
                                borderRadius: 2,
                                marginBottom: 8
                              }} 
                            />
                            <div style={{ fontSize: 12, color: '#666' }}>
                              {['周一', '周二', '周三', '周四', '周五', '周六', '周日'][index]}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ textAlign: 'center', marginTop: 8, color: '#666', fontSize: 12 }}>
                        本周对话量趋势
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </Tabs.TabPane>
          </Tabs>
        )}
      </Modal>
    </div>
  );
};

export default Reports;
